// src/components/ReportIssueModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { X, AlertTriangle, Send, LoaderCircle, Mail } from 'lucide-react';

// Lista de categorias de fácil alteração
const errorCategoriesLoggedIn = [
  "Problema com a câmera", "A foto não está validando", "Problema com login",
  "Sem acesso/permissão", "Problema de conexão", "Fotos não aparecem", "Outro"
];
const errorCategoriesLoggedOut = [
  "Problema com login", "Sem acesso/permissão", "Problema de conexão", "Outro"
];

export default function ReportIssueModal({ isVisible, onClose, screenName }) {
  const { user } = useUser();
  
  const [formData, setFormData] = useState({
    email: '',
    categoria: '',
    descricao: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Determina se o usuário está logado para exibir o formulário correto
  const isLoggedIn = !!user;

  // Preenche o email automaticamente se o usuário estiver logado
  useEffect(() => {
    if (isLoggedIn) {
      setFormData(prev => ({ ...prev, email: user.email }));
    } else {
      setFormData(prev => ({...prev, email: ''})); // Limpa o email ao abrir se não estiver logado
    }
  }, [isLoggedIn, user, isVisible]);
  
  // Reseta o formulário quando o modal é fechado
  useEffect(() => {
    if (!isVisible) {
      setTimeout(() => {
          setFormData({ email: isLoggedIn ? user.email : '', categoria: '', descricao: '' });
          setError('');
          setSuccess(false);
          setIsLoading(false);
      }, 300); // Delay para a animação de saída
    }
  }, [isVisible, isLoggedIn, user]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validação
    if (!formData.categoria || !formData.descricao.trim() || (!isLoggedIn && !formData.email.trim())) {
      setError('Todos os campos são obrigatórios.');
      return;
    }
    if (!isLoggedIn) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Por favor, insira um formato de e-mail válido.');
            return;
        }
    }

    setIsLoading(true);
    try {
      await axios.post('/api/report-issue', {
        ...formData,
        tela_report: screenName
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível enviar o seu relato. Tente novamente ou contate o seu supervisor.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-sm transform transition-all duration-300 ease-out scale-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Relatar Problema</h2>
          <button onClick={onClose} disabled={isLoading} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200">
            <X size={20} className="text-gray-600" />
          </button>
        </div>
        
        {success ? (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-green-600 mb-2">Problema reportado com sucesso!</h3>
            <p className="text-gray-600 text-sm">
              <strong>Prossiga com o recebimento normalmente.</strong> O time responsável irá verificar o problema e retornará assim que possível. 
              Em caso de dúvidas, contate seu supervisor.
            </p>
            <button onClick={onClose} className="mt-6 w-full bg-[#EE4D2D] text-white font-bold py-2 rounded-lg">
                Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {!isLoggedIn && (
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    placeholder="Seu e-mail" disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]"
                  />
                </div>
              )}
              <div className="relative">
                <AlertTriangle size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  name="categoria" value={formData.categoria} onChange={handleChange}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]"
                >
                  <option value="" disabled>Selecione uma categoria</option>
                  {(isLoggedIn ? errorCategoriesLoggedIn : errorCategoriesLoggedOut).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <textarea
                  name="descricao" value={formData.descricao} onChange={handleChange}
                  placeholder="Descreva o problema..." maxLength="1000" rows="4"
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]"
                />
              </div>
            </div>

            {error && <p className="text-red-500 text-sm mt-4 text-center">{error}</p>}

            <button
              type="submit" disabled={isLoading}
              className="mt-6 w-full flex justify-center items-center bg-[#EE4D2D] text-white font-bold py-3 rounded-lg hover:bg-[#D03F2A] transition-colors disabled:bg-red-300"
            >
              {isLoading ? <LoaderCircle className="animate-spin" /> : <><Send size={18} className="mr-2"/> Enviar</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}